import {
  IQueryCommandOptions,
  In,
  Like,
  GreaterThanOrEqual,
  LessThanOrEqual,
} from "@llws/dynamic-router-utils";
import {
  Grid,
  Paper,
  TableContainer,
  Table as MuiTable,
  Checkbox,
  TableHead,
  TableBody,
  Button,
  Box,
  TablePagination,
  CircularProgress,
  TextField,
  Typography,
  Chip,
  FormControl,
  Select,
  MenuItem,
  ListItemText,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import ClearIcon from '@mui/icons-material/Clear';
import { StyledTableRow, StyledTableCell } from "src/components/parts/mui/StyledTables";
import { getMany } from "src/redux/features/dynamic-hydra/actions";
import { useAppSelector } from "src/redux/hooks";
import React from "react";
import { intVal } from "@jamesgmarks/utilities";
import { rowsPerPageList } from "../Clients/Tabs";
import { ReplayWebhooksModal } from "./ReplayWebhookModal";
import { WebhookCallbackRow } from "./WebhookCallbackRow";
import { HHMultiStateToggle } from "src/components/parts/HHMultiStateToggle";
import { performApiActionRequest } from "src/redux/apiActionRequest";

export const paginationStyling = {
  '& p': { marginTop: '1rem' },
  backgroundColor: '#f2f2f2',
  '& .MuiTablePagination-actions > button': {
    '&:hover': {
      backgroundColor: '#e0e0e0',
    },
  },
};

export const WebhookCallbackManagement = () => {
  const webhookCallbackList = useAppSelector(state => state.dynamicHydra.data.webhook_callbacks?.list ?? []);
  const loadedState = useAppSelector(state => state.dynamicHydra.data.webhook_callbacks?.loadedState);
  const metaData = useAppSelector(state => state.dynamicHydra.metaData);

  const [sortColumn, setSortColumn] = useState('id');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState<number>(0);
  const [limit, setLimit] = useState<number>(20);
  const [selectedWebhookCallback, setSelectedWebhookCallback] = useState<number[]>([]);
  const [isReplayModalOpen, setIsReplayModalOpen] = useState(false);
  const [webhookSourceType, setWebhookSourceType] = useState<string[]>([]);

  // Filter states
  const [searchId, setSearchId] = useState<string>('');
  const [debouncedSearchId, setDebouncedSearchId] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [dateRangeFilter, setDateRangeFilter] = useState({
    receivedDateFrom: '',
    receivedDateTo: '',
    replayedDateFrom: '',
    replayedDateTo: '',
  });
  const [showAdvancedFilters, setShowAdvancedFilters] = useState<boolean>(false);

  // Debounce search ID
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchId(searchId);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchId]);

  // Reset page when filters change
  const resetPageOnFilterChange = useCallback(() => {
    setPage(0);
  }, []);

  useEffect(resetPageOnFilterChange, [
    debouncedSearchId,
    statusFilter,
    dateRangeFilter,
    webhookSourceType,
    resetPageOnFilterChange,
  ]);

  const toggleOptions = useMemo(() => (
    [
      {
        value: 'xero',
        label: 'Xero',
      },
      {
        value: 'freshbooks',
        label: 'Freshbooks',
      },
      {
        value: 'stripe',
        label: 'Stripe',
      },
    ]
  ), []);

  const statusOptions = useMemo(() => [
    { value: 'completed', label: 'Completed' },
    { value: 'processing', label: 'Processing' },
    { value: 'errored', label: 'Errored' },
    { value: 'queued', label: 'Queued' },
  ], []);

  const queryCommand: IQueryCommandOptions = useMemo(() => {
    const whereConditions = [
      // Source type filter
      ...(webhookSourceType.length > 0 ? [In('sourceType', webhookSourceType)] : []),

      // ID search filter
      ...(debouncedSearchId ? [Like('id', `%${debouncedSearchId}%`)] : []),

      // Status filter
      ...(statusFilter.length > 0 ? [In('status', statusFilter)] : []),

      // Date range filters
      ...(dateRangeFilter.receivedDateFrom ? [GreaterThanOrEqual('created', dateRangeFilter.receivedDateFrom)] : []),
      ...(dateRangeFilter.receivedDateTo ? [LessThanOrEqual('created', dateRangeFilter.receivedDateTo)] : []),
      ...(dateRangeFilter.replayedDateFrom ? [GreaterThanOrEqual('modified', dateRangeFilter.replayedDateFrom)] : []),
      ...(dateRangeFilter.replayedDateTo ? [LessThanOrEqual('modified', dateRangeFilter.replayedDateTo)] : []),
    ];

    return {
      alias: 'webhookCallback',
      page: page + 1,
      limit,
      where: whereConditions,
      orderBy: [{ field: sortColumn, direction: sortDirection }],
    };
  }, [page, limit, sortColumn, sortDirection, webhookSourceType, debouncedSearchId, statusFilter, dateRangeFilter]);

  useEffect(() => {
    getMany('webhook_callbacks', queryCommand);
  }, [queryCommand]);

  const sortBy = (criteria: string) => {
    setSortDirection(sortColumn === criteria && sortDirection === 'asc' ? 'desc' : 'asc');
    setSortColumn(criteria);
  };

  const toggleAllCheckbox = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    if (checked) {
      const allIds = webhookCallbackList.map(callback => callback.id);
      setSelectedWebhookCallback(allIds);
    } else {
      setSelectedWebhookCallback([]);
    }
  }, [webhookCallbackList]);

  const toggleRowCheckbox = useCallback((webhookCallbackId: number) => {
    setSelectedWebhookCallback(prev => {
      const isSelected = prev.includes(webhookCallbackId);
      if (isSelected) {
        return prev.filter(id => id !== webhookCallbackId);
      } else {
        return [...prev, webhookCallbackId];
      }
    });
  }, []);

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setLimit(intVal(event.target.value));
    setPage(0);
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleReplayClick = (id?: number) => {
    setIsReplayModalOpen(true);
    if (id) {
      setSelectedWebhookCallback([id]);
    }
  };

  const handleConfirmReplay = async () => {
    await performApiActionRequest('replayXeroWebhook', {
      webhookCallBackIds: selectedWebhookCallback,
      replayAllFailed: false,
    });

    setSelectedWebhookCallback([]);
    setIsReplayModalOpen(false);
  };

  return (
    <Grid container spacing={2} mt={2} justifyContent="center" alignItems="center">
      <Grid item xs={12}>
        {/* Filters Card */}
        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          {/* Search and Filter Section */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={2}>
            <Box display="flex" alignItems="center" gap={2} flexGrow={1}>
              <TextField
                size="small"
                placeholder="Search by ID..."
                value={searchId}
                onChange={(e) => setSearchId(e.target.value)}
                sx={{ minWidth: 200 }}
                InputProps={{
                  startAdornment: (
                    <Typography variant="body2" color="textSecondary" sx={{ mr: 1 }}>
                      🔍
                    </Typography>
                  ),
                  endAdornment: searchId && (
                    <ClearIcon
                      fontSize="small"
                      sx={{ cursor: 'pointer', color: 'action.active' }}
                      onClick={() => setSearchId('')}
                    />
                  ),
                }}
              />
              <HHMultiStateToggle
                key={webhookSourceType.join(',')}
                options={toggleOptions}
                defaultValues={webhookSourceType}
                onChange={(value: string | string[]) => {
                  if (!Array.isArray(value)) {
                    return;
                  }
                  setWebhookSourceType(value);
                }}
                multiple={true}
              />
              <Button
                variant={showAdvancedFilters ? "contained" : "outlined"}
                color="secondary"
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                sx={{ ml: 1 }}
              >
                {showAdvancedFilters ? "Hide" : "Show"} Advanced Filters
              </Button>
            </Box>
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleReplayClick()}
              disabled={selectedWebhookCallback.length === 0}
            >
              Replay {selectedWebhookCallback.length} Webhook{selectedWebhookCallback.length !== 1 ? 's' : ''}
            </Button>
          </Box>

          {/* Advanced Filters Section */}
          {showAdvancedFilters && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold" mb={2}>
                Advanced Filters
              </Typography>
              <Grid container spacing={2}>
                {/* Status Filter */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <Typography variant="body2" fontWeight="bold" mb={1}>
                      Status
                    </Typography>
                    <Select
                      multiple
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value as string[])}
                      displayEmpty
                      renderValue={(selected) => {
                        if (selected.length === 0) {
                          return <Typography color="textSecondary">All statuses</Typography>;
                        }
                        return selected.join(', ');
                      }}
                    >
                      {statusOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          <Checkbox checked={statusFilter.includes(option.value)} />
                          <ListItemText primary={option.label} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Date Range Filters */}
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" fontWeight="bold" mb={1}>
                    Received Date Range
                  </Typography>
                  <Box display="flex" gap={1}>
                    <TextField
                      size="small"
                      type="date"
                      label="From"
                      value={dateRangeFilter.receivedDateFrom}
                      onChange={(e) => setDateRangeFilter(prev => ({ ...prev, receivedDateFrom: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      size="small"
                      type="date"
                      label="To"
                      value={dateRangeFilter.receivedDateTo}
                      onChange={(e) => setDateRangeFilter(prev => ({ ...prev, receivedDateTo: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                      sx={{ flex: 1 }}
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="body2" fontWeight="bold" mb={1}>
                    Replayed Date Range
                  </Typography>
                  <Box display="flex" gap={1}>
                    <TextField
                      size="small"
                      type="date"
                      label="From"
                      value={dateRangeFilter.replayedDateFrom}
                      onChange={(e) => setDateRangeFilter(prev => ({ ...prev, replayedDateFrom: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      size="small"
                      type="date"
                      label="To"
                      value={dateRangeFilter.replayedDateTo}
                      onChange={(e) => setDateRangeFilter(prev => ({ ...prev, replayedDateTo: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                      sx={{ flex: 1 }}
                    />
                  </Box>
                </Grid>

                {/* Clear All Filters Button */}
                <Grid item xs={12}>
                  <Box display="flex" justifyContent="flex-end">
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={() => {
                        setSearchId('');
                        setStatusFilter([]);
                        setDateRangeFilter({
                          receivedDateFrom: '',
                          receivedDateTo: '',
                          replayedDateFrom: '',
                          replayedDateTo: '',
                        });
                        setWebhookSourceType([]);
                      }}
                      disabled={
                        !debouncedSearchId &&
                        statusFilter.length === 0 &&
                        !dateRangeFilter.receivedDateFrom &&
                        !dateRangeFilter.receivedDateTo &&
                        !dateRangeFilter.replayedDateFrom &&
                        !dateRangeFilter.replayedDateTo &&
                        webhookSourceType.length === 0
                      }
                    >
                      Clear All Filters
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Active Filters Display */}
          {
            (debouncedSearchId || statusFilter.length > 0 ||
              dateRangeFilter.receivedDateFrom || dateRangeFilter.receivedDateTo ||
              dateRangeFilter.replayedDateFrom || dateRangeFilter.replayedDateTo ||
              webhookSourceType.length > 0) && (
              <Box display="flex" flexWrap="wrap" gap={1} mt={2}>
                {debouncedSearchId && (
                  <Chip
                    label={`ID: ${debouncedSearchId}`}
                    onDelete={() => setSearchId('')}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
                {statusFilter.length > 0 && (
                  <Chip
                    label={`Status: ${statusFilter.join(', ')}`}
                    onDelete={() => setStatusFilter([])}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
                {dateRangeFilter.receivedDateFrom && (
                  <Chip
                    label={`Received From: ${dateRangeFilter.receivedDateFrom}`}
                    onDelete={() => setDateRangeFilter(prev => ({ ...prev, receivedDateFrom: '' }))}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
                {dateRangeFilter.receivedDateTo && (
                  <Chip
                    label={`Received To: ${dateRangeFilter.receivedDateTo}`}
                    onDelete={() => setDateRangeFilter(prev => ({ ...prev, receivedDateTo: '' }))}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
                {dateRangeFilter.replayedDateFrom && (
                  <Chip
                    label={`Replayed From: ${dateRangeFilter.replayedDateFrom}`}
                    onDelete={() => setDateRangeFilter(prev => ({ ...prev, replayedDateFrom: '' }))}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
                {dateRangeFilter.replayedDateTo && (
                  <Chip
                    label={`Replayed To: ${dateRangeFilter.replayedDateTo}`}
                    onDelete={() => setDateRangeFilter(prev => ({ ...prev, replayedDateTo: '' }))}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
                {webhookSourceType.length > 0 && (
                  <Chip
                    label={`Source: ${webhookSourceType.join(', ')}`}
                    onDelete={() => setWebhookSourceType([])}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
              </Box>
            )}
        </Paper>

        <TablePagination
          component="div"
          sx={paginationStyling}
          count={metaData?.totalRecords ?? 0}
          page={page}
          rowsPerPage={limit}
          showFirstButton
          showLastButton
          rowsPerPageOptions={rowsPerPageList}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
        <TableContainer component={Paper} elevation={12} sx={{ mb: 1, maxHeight: '70vh', overflow: 'auto' }}>
          <MuiTable stickyHeader style={{ borderCollapse: 'collapse' }}>
            <TableHead>
              <StyledTableRow dark={true} >
                <StyledTableCell
                  dark={true}
                  style={{ textAlign: 'center' }}
                >
                  <Checkbox
                    style={{ color: 'white' }}
                    indeterminate={
                      selectedWebhookCallback.length > 0 && selectedWebhookCallback.length < webhookCallbackList.length
                    }
                    checked={
                      webhookCallbackList.length > 0 && selectedWebhookCallback.length === webhookCallbackList.length
                    }
                    onChange={toggleAllCheckbox}
                  />
                </StyledTableCell>
                <StyledTableCell
                  dark={true}
                  style={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}
                  onClick={() => sortBy('id')}
                >
                  Callback ID
                </StyledTableCell>
                <StyledTableCell
                  dark={true}
                  style={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}
                  onClick={() => sortBy('name')}
                >
                  Name
                </StyledTableCell>
                <StyledTableCell
                  dark={true}
                  style={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}
                  onClick={() => sortBy('objectId')}
                >
                  Object ID
                </StyledTableCell>
                <StyledTableCell
                  dark={true}
                  style={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}
                  onClick={() => sortBy('sourceType')}
                >
                  Source
                </StyledTableCell>
                <StyledTableCell
                  dark={true}
                  style={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}
                  onClick={() => sortBy('status')}
                >
                  Status
                </StyledTableCell>
                <StyledTableCell
                  dark={true}
                  style={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}
                  onClick={() => sortBy('created')}
                >
                  Received Date
                </StyledTableCell>
                <StyledTableCell
                  dark={true}
                  style={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}
                  onClick={() => sortBy('modified')}
                >
                  Last Replayed Date
                </StyledTableCell>
                <StyledTableCell
                  dark={true}
                  style={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}
                  onClick={() => sortBy('modifiedUserId')}
                >
                  Replayed By
                </StyledTableCell>
                <StyledTableCell>&nbsp;</StyledTableCell>
              </StyledTableRow>
            </TableHead>
            <TableBody>
              {(loadedState === 'loading'
                ? []
                : (webhookCallbackList ?? []))
                .map((i) => (
                  <WebhookCallbackRow
                    key={i.id}
                    callbackData={i}
                    checked={selectedWebhookCallback.includes(i.id)}
                    onToggle={toggleRowCheckbox}
                    onReplay={handleReplayClick}
                  />
                ))
              }
            </TableBody>
          </MuiTable>
          {loadedState === 'loading'
            ? <Box sx={{ textAlign: 'center' }} pt={2}>
              <CircularProgress size={25} color='primary' sx={{ ml: 2 }} />
            </Box>
            : webhookCallbackList?.length === 0 && <Box sx={{ textAlign: 'center' }}>NO DATA AVAILABLE</Box>
          }
          <TablePagination
            component="div"
            sx={paginationStyling}
            count={metaData?.totalRecords ?? 0}
            page={page}
            rowsPerPage={limit}
            showFirstButton
            showLastButton
            rowsPerPageOptions={rowsPerPageList}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </TableContainer>
      </Grid>
      <ReplayWebhooksModal
        open={isReplayModalOpen}
        selectedIds={selectedWebhookCallback}
        onClose={() => setIsReplayModalOpen(false)}
        onConfirm={handleConfirmReplay}
      />
    </Grid>
  );
};